import type { Ticket, ZReportTicket, KitchenTicket } from '../../types';
import type { BtDevice } from '../bt/BluetoothBridge';

export type PrinterVendor = 'generic' | 'sunmi' | 'epson' | 'geon' | 'star';
export type PrinterProtocol =
  | 'spp_escpos'
  | 'sunmi_cloud'
  | 'epson_epos'
  | 'geon_native'
  | 'star_stario';

export type PrinterTransport = 'bluetooth' | 'network' | 'usb';

export interface PrinterStatus {
  online: boolean;
  paperOut?: boolean;
  coverOpen?: boolean;
  overheated?: boolean;
  raw?: any;
}

export interface PrinterAdapter {
  vendor: PrinterVendor;
  protocol: PrinterProtocol;

  connect(target: { mac?: string; ip?: string; port?: number }): Promise<void>;
  disconnect(): Promise<void>;

  printReceipt(ticket: Ticket): Promise<void>;
  printZReport?(report: ZReportTicket): Promise<void>;
  printKitchenOrder?(order: KitchenTicket): Promise<void>;
  printRaw(data: Uint8Array): Promise<void>;

  openCashDrawer?(pin?: 2 | 5): Promise<void>;
  getStatus?(): Promise<PrinterStatus>;
  beep?(): Promise<void>;
}

/** Vendor auto-detection from BT device name or MAC OUI. */
export function detectVendor(device: BtDevice): PrinterVendor {
  const name = (device.name || '').toLowerCase();
  const mac = (device.address || '').toUpperCase().replace(/:/g, '');
  const oui = mac.substring(0, 6);

  if (name.startsWith('sunmi') || name.includes('cloudprint') || oui === 'B47774') return 'sunmi';
  if (name.startsWith('tm-') || name.includes('epson') || oui === '0026AB' || oui === '6417E8')
    return 'epson';
  if (name.startsWith('geon') || name.startsWith('gs-')) return 'geon';
  if (name.startsWith('star') || name.startsWith('mc-print') || oui === '001162') return 'star';
  return 'generic';
}

export function protocolForVendor(v: PrinterVendor): PrinterProtocol {
  switch (v) {
    case 'sunmi':
      return 'sunmi_cloud';
    case 'epson':
      return 'epson_epos';
    case 'geon':
      return 'geon_native';
    case 'star':
      return 'star_stario';
    default:
      return 'spp_escpos';
  }
}

export function vendorLabel(v: PrinterVendor): string {
  switch (v) {
    case 'sunmi':
      return 'Sunmi';
    case 'epson':
      return 'Epson';
    case 'geon':
      return 'Géon';
    case 'star':
      return 'Star';
    default:
      return 'Générique';
  }
}
